import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { dateText, SharedTagsResponse } from './api'
import styles from './GodMode.module.css'

const BASIS: Record<string, string> = { client: 'Client number', legacy: 'Legacy packhouse', first_use: 'First use' }

// Tags two or more clients have used. A tag belongs to the client whose
// number it ends in, or the legacy packhouse that printed it, else the one
// that used it first; every other client scanning it is using it without the
// co-mingling module.
export function SharedTagsPage() {
  const { user } = useAuth()
  const [data, setData] = useState<SharedTagsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    apiJson<SharedTagsResponse>(user, '/api/god-mode/shared-tags')
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message))
    return () => {
      live = false
    }
  }, [user])

  return (
    <>
      <PageHeader title="Shared tags" />
      <PageBody>
        <div className={styles.stack}>
          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <div className={styles.tableCard}>
              {data.tags.length === 0 ? (
                <div className={styles.empty}>No client has used another client's tags.</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Owner</th>
                      <th>Basis</th>
                      <th>Also used by</th>
                      <th>Their scans</th>
                      <th>Latest first use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tags.map((t) => (
                      <tr key={t.code}>
                        <td title={t.code}>
                          {t.label}
                          {t.label !== t.code && <div className={styles.lead}><code>{t.code}</code></div>}
                        </td>
                        <td className={styles.owner}>
                          {t.owners.map((o, i) => (
                            <span key={o.tenant_id || `legacy-${o.legacy_packhouse_id}`}>
                              {i > 0 && ', '}
                              {o.tenant_id ? (
                                <Link to={`/god-mode/clients/${o.tenant_id}`}>{o.name}</Link>
                              ) : (
                                <Link to="/god-mode/legacy" title="A legacy client not yet moved to CONTROL">
                                  {o.name} ({o.legacy_packhouse_id})
                                </Link>
                              )}
                            </span>
                          ))}
                        </td>
                        <td>{BASIS[t.basis] || t.basis}</td>
                        <td className={styles.other}>
                          {t.tenants
                            .filter((u) => !u.owner)
                            .map((u, i) => (
                              <span key={u.tenant_id} title={`First used ${dateText(u.first_scan)}, ${u.scans} scan${u.scans === 1 ? '' : 's'}`}>
                                {i > 0 && ', '}
                                <Link to={`/god-mode/clients/${u.tenant_id}`}>{u.name}</Link> ({dateText(u.first_scan)})
                              </span>
                            ))}
                        </td>
                        <td>{t.unauthorised_scans}</td>
                        <td>{dateText(t.last_first_scan)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <p className={styles.lead}>
            A client's tags end in its number, so whoever scans them, they are its own. Tags with no number (Dataphyll's tag stock)
            belong to the first client to scan them.
          </p>
        </div>
      </PageBody>
    </>
  )
}
